type ClientCrypto = {
  randomUUID?: () => string;
  getRandomValues?: (values: Uint8Array<ArrayBuffer>) => void;
};

function runtimeCrypto(): ClientCrypto | undefined {
  const source = globalThis.crypto;
  if (!source) return undefined;
  return {
    randomUUID: typeof source.randomUUID === "function" ? () => source.randomUUID() : undefined,
    getRandomValues: typeof source.getRandomValues === "function"
      ? (values) => { source.getRandomValues(values); }
      : undefined,
  };
}

/**
 * Creates a UUID-shaped client request identifier on both secure origins and
 * LAN-only HTTP origins. `crypto.randomUUID()` is restricted to secure
 * contexts in some mobile browsers, while `getRandomValues()` remains
 * available there. The final fallback is sufficient for UI/idempotency keys;
 * these identifiers are not authentication secrets.
 */
export function createClientId(source: ClientCrypto | null | undefined = runtimeCrypto()): string {
  if (typeof source?.randomUUID === "function") {
    return source.randomUUID();
  }

  const bytes = new Uint8Array(new ArrayBuffer(16));
  if (typeof source?.getRandomValues === "function") {
    source.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  // RFC 4122 version 4 and variant bits keep the fallback UUID-shaped.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}
